/**
 * VariableInspectorEditor
 *
 * Property editor for variable-inspector control type
 * Configures variable display, filtering, and inspection features
 *
 * SRP: Responsible ONLY for configuring variable-inspector properties
 * Control Type: 'variable-inspector'
 *
 * Properties Configured (6):
 * 1. showDataTypes - Display variable data types
 * 2. showMethods - Show object methods
 * 3. searchable - Enable search filter
 * 4. expandByDefault - Expand all objects initially
 * 5. backgroundColor - Background color
 * 6. highlightColor - Highlight color for search results
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';

/**
 * Variable inspector configuration
 */
interface VariableInspectorConfig {
  showDataTypes?: boolean;
  showMethods?: boolean;
  searchable?: boolean;
  expandByDefault?: boolean;
  backgroundColor?: string;
  highlightColor?: string;
}

/**
 * VariableInspectorEditor Component
 * Renders property editors for all variable-inspector configuration
 */
const VariableInspectorEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as VariableInspectorConfig) || {},
    [control.config],
  );

  const handleShowDataTypesChange = useCallback(
    (value: boolean) => onConfigChange('showDataTypes', value),
    [onConfigChange],
  );

  const handleShowMethodsChange = useCallback(
    (value: boolean) => onConfigChange('showMethods', value),
    [onConfigChange],
  );

  const handleSearchableChange = useCallback(
    (value: boolean) => onConfigChange('searchable', value),
    [onConfigChange],
  );

  const handleExpandByDefaultChange = useCallback(
    (value: boolean) => onConfigChange('expandByDefault', value),
    [onConfigChange],
  );

  const handleBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('backgroundColor', value),
    [onConfigChange],
  );

  const handleHighlightColorChange = useCallback(
    (value: string) => onConfigChange('highlightColor', value),
    [onConfigChange],
  );

  return (
    <div className="af-property-editor__section variable-inspector-editor">
      <h3 className="af-property-editor__section-title">Variable Inspector Properties</h3>

      <CheckboxPropertyEditor
        label="Show Data Types"
        value={config.showDataTypes ?? true}
        onChange={handleShowDataTypesChange}
        helpText="Display data type for each variable"
      />

      <CheckboxPropertyEditor
        label="Show Methods"
        value={config.showMethods || false}
        onChange={handleShowMethodsChange}
        helpText="Display object methods alongside properties"
      />

      <CheckboxPropertyEditor
        label="Searchable"
        value={config.searchable ?? true}
        onChange={handleSearchableChange}
        helpText="Enable search/filter functionality"
      />

      <CheckboxPropertyEditor
        label="Expand by Default"
        value={config.expandByDefault || false}
        onChange={handleExpandByDefaultChange}
        helpText="Expand all objects and arrays initially"
      />

      <ColorPropertyEditor
        label="Background Color"
        value={config.backgroundColor || '#FFFFFF'}
        onChange={handleBackgroundColorChange}
        helpText="Inspector background color"
      />

      <ColorPropertyEditor
        label="Highlight Color"
        value={config.highlightColor || '#FFFF00'}
        onChange={handleHighlightColorChange}
        helpText="Color for search result highlighting"
      />
    </div>
  );
};

VariableInspectorEditorComponent.displayName = 'VariableInspectorEditor';

/**
 * Variable Inspector Property Editor Registration
 */
export const VariableInspectorEditor: PropertyEditor = {
  type: 'variable-inspector',
  label: 'Variable Inspector',
  description: 'Configure variable display, filtering, and inspection features',
  version: '1.0.0',
  render: (props) => <VariableInspectorEditorComponent {...props} />,
};

export default VariableInspectorEditor;
